import React, { useCallback, useEffect, useMemo, useState } from "react"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import TextField from "@mui/material/TextField"
import IconButton from "@mui/material/IconButton"
import Autocomplete from "@mui/material/Autocomplete"
import Tooltip from "@mui/material/Tooltip"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import SaveRoundedIcon from "@mui/icons-material/SaveRounded"
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded"
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded"
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded"
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import {
  getOnboardingWelcomeVideoConfig,
  saveOnboardingWelcomeVideoConfig,
} from "services/modal"
import { listProductGates } from "services/productGates"
import { verifyResponse } from "utils/httpRequest"
import { useSnackbar } from "notistack"
import { StyledModal } from "../Modal/Modal.style"

const TIER_KEYS = ["free", "silver", "gold", "platinum"]

const TIER_LABELS = {
  free: "Free",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum",
}

const getDefaultWelcomeVideoConfig = () => ({
  tiers: {
    free: "1126188033",
    silver: "1126188408",
    gold: "1126188660",
    platinum: "1126189283",
  },
  productDefault: "1126190219",
  products: [
    {
      productGateId: 1,
      label: "Winning the Game of Business",
      vimeoId: "1126190219",
    },
  ],
})

const parseVimeoIdValue = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return ""
  const idOnly = raw.match(/^(\d{6,})$/)
  if (idOnly) return idOnly[1]
  const playerMatch = raw.match(/player\.vimeo\.com\/video\/(\d{6,})/i)
  if (playerMatch) return playerMatch[1]
  const eventMatch = raw.match(/vimeo\.com\/event\/(\d{6,})/i)
  if (eventMatch) return eventMatch[1]
  const vimeoMatch = raw.match(/vimeo\.com\/(?:channels\/[^/]+\/)?(\d{6,})/i)
  if (vimeoMatch) return vimeoMatch[1]
  const digits = raw.replace(/[^0-9]/g, "")
  return digits.length >= 6 ? digits : ""
}

const normalizeWelcomeVideoConfig = (raw) => {
  const fallback = getDefaultWelcomeVideoConfig()
  const tiersRaw = (raw && raw.tiers) || {}
  const tiers = {}
  for (const key of TIER_KEYS) {
    tiers[key] = parseVimeoIdValue(tiersRaw[key]) || fallback.tiers[key] || ""
  }
  const productDefault =
    parseVimeoIdValue(raw && raw.productDefault) || fallback.productDefault || ""
  const productsSource = Array.isArray(raw && raw.products) ? raw.products : []
  const products = productsSource
    .map((item) => ({
      productGateId: Math.max(0, Math.floor(Number(item?.productGateId || 0))),
      label: String(item?.label || "").trim(),
      vimeoId: parseVimeoIdValue(item?.vimeoId),
    }))
    .filter((item) => item.productGateId > 0 && item.vimeoId)
  return { tiers, productDefault, products }
}

const resolveWelcomeVideoResponseConfig = (response, fallback) => {
  const candidates = [
    response?.data?.config,
    response?.config,
    response?.data?.data?.config,
    response?.data?.data,
    response?.data,
    response,
  ]
  for (const candidate of candidates) {
    if (!candidate) continue
    return normalizeWelcomeVideoConfig(candidate)
  }
  return normalizeWelcomeVideoConfig(fallback || getDefaultWelcomeVideoConfig())
}

const resolveResponseUpdatedAt = (response) =>
  response?.data?.updatedAt ||
  response?.data?.data?.updatedAt ||
  response?.updatedAt ||
  ""

const formatRelativeTime = (value) => {
  if (!value) return ""
  const then = new Date(value).getTime()
  if (!Number.isFinite(then)) return ""
  const diffMs = Date.now() - then
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`
  return new Date(value).toLocaleDateString()
}

function VideoTile({
  title,
  pillLabel,
  pillKind,
  rawValue,
  onChange,
  onRemove,
  removable,
  onPreview,
  canEdit,
  placeholder,
  helperEmpty,
}) {
  const vimeoId = parseVimeoIdValue(rawValue)
  const status = (() => {
    const trimmed = String(rawValue || "").trim()
    if (!trimmed) return "empty"
    if (vimeoId) return "ok"
    return "warn"
  })()
  const showInlinePreview = !!vimeoId

  return (
    <div className={`video-tile ${vimeoId ? "" : "video-tile-empty"}`.trim()}>
      <div className="video-tile-head">
        <div className="video-tile-title" title={title}>
          <span className={pillKind || "tier-pill"}>{pillLabel}</span>
          <span className="video-tile-title-text">{title}</span>
        </div>
        <div className="video-tile-actions">
          {vimeoId && (
            <Tooltip title="Preview video">
              <IconButton size="small" onClick={() => onPreview && onPreview({ title, vimeoId })}>
                <PlayCircleOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {vimeoId && (
            <Tooltip title="Open on vimeo.com">
              <IconButton
                size="small"
                component="a"
                href={`https://vimeo.com/${vimeoId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {removable && canEdit && (
            <Tooltip title="Remove">
              <IconButton size="small" onClick={onRemove}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="video-preview">
        {showInlinePreview ? (
          <iframe
            key={vimeoId}
            src={`https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&dnt=1`}
            title={title}
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <div className="video-preview-placeholder">
            <span className="placeholder-icon">
              <PlayCircleOutlineRoundedIcon style={{ color: "var(--cms-text-secondary)" }} />
            </span>
            <span>No video selected yet</span>
          </div>
        )}
      </div>

      <div className="video-tile-input">
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder || "Paste a Vimeo URL or share link"}
          value={rawValue || ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={!canEdit}
        />
      </div>
      <div
        className={`video-status ${
          status === "ok"
            ? "video-status-ok"
            : status === "warn"
            ? "video-status-warn"
            : "video-status-empty"
        }`}
      >
        {status === "ok" && (
          <>
            <CheckCircleRoundedIcon fontSize="inherit" />
            <span>Connected · vimeo.com/{vimeoId}</span>
          </>
        )}
        {status === "warn" && (
          <>
            <ErrorOutlineRoundedIcon fontSize="inherit" />
            <span>That doesn't look like a Vimeo link. Paste the full URL.</span>
          </>
        )}
        {status === "empty" && (
          <>
            <ErrorOutlineRoundedIcon fontSize="inherit" />
            <span>{helperEmpty || "Paste a Vimeo URL or share link to set the video."}</span>
          </>
        )}
      </div>
    </div>
  )
}

function WelcomeVideoSection({
  config,
  productGates,
  productGatesById,
  productGatesLoading,
  usedProductGateIds,
  updatedAt,
  loading,
  saving,
  dirty,
  canEdit,
  onChangeTier,
  onChangeProductDefault,
  onChangeProduct,
  onRemoveProduct,
  onAddProduct,
  onSave,
  onReset,
  onPreview,
}) {
  const tieredProducts = (config?.products || [])
  const totalConfigured =
    TIER_KEYS.filter((key) => parseVimeoIdValue(config?.tiers?.[key])).length +
    (parseVimeoIdValue(config?.productDefault) ? 1 : 0) +
    tieredProducts.filter((p) => parseVimeoIdValue(p.vimeoId) && Number(p.productGateId) > 0)
      .length

  const handleSelectProduct = (index, gate) => {
    const id = Number(gate?.id || 0)
    onChangeProduct(index, "productGateId", id)
    if (gate?.name) onChangeProduct(index, "label", gate.name)
  }

  return (
    <div className="welcome-card">
      <div className="welcome-card-head">
        <div>
          <h3 className="welcome-card-title">
            <span className="welcome-card-title-pill">Step 1</span>
            Welcome Videos
          </h3>
          <p className="welcome-card-subtitle">
            Pick the video that plays on the very first onboarding screen for each plan and program.
            Just paste any Vimeo link — like <code>https://vimeo.com/1126188033</code> — and you'll
            see an instant preview. The site shows the right video automatically based on what each
            member has access to.
          </p>
        </div>
        <div className="welcome-meta">
          <Tooltip title="Number of videos currently set">
            <span>
              <strong>{totalConfigured}</strong> video{totalConfigured === 1 ? "" : "s"} set
            </span>
          </Tooltip>
          {updatedAt ? (
            <Tooltip title={new Date(updatedAt).toLocaleString()}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <HistoryRoundedIcon style={{ fontSize: 14 }} />
                Updated {formatRelativeTime(updatedAt)}
              </span>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <div className="welcome-section-label">Membership plans</div>
      <div className="welcome-tiers-grid">
        {TIER_KEYS.map((tierKey) => (
          <VideoTile
            key={`tile-tier-${tierKey}`}
            title={`${TIER_LABELS[tierKey]} members see this video`}
            pillLabel={TIER_LABELS[tierKey]}
            pillKind={`tier-pill tier-${tierKey}`}
            rawValue={config?.tiers?.[tierKey] || ""}
            onChange={(value) => onChangeTier(tierKey, value)}
            canEdit={canEdit}
            onPreview={onPreview}
          />
        ))}
      </div>

      <div className="welcome-section-label">Programs &amp; products</div>
      <div className="welcome-products-grid">
        <VideoTile
          title="Default video for any other program"
          pillLabel="Fallback"
          pillKind="tier-pill tier-fallback"
          rawValue={config?.productDefault || ""}
          onChange={(value) => onChangeProductDefault(value)}
          canEdit={canEdit}
          onPreview={onPreview}
          helperEmpty="Optional. Plays for members who own a program that isn't listed below."
        />
        {tieredProducts.map((product, index) => {
          const gate = productGatesById?.get(Number(product.productGateId))
          const productOptions = (productGates || []).filter(
            (g) =>
              g.id === Number(product.productGateId) ||
              !usedProductGateIds.has(Number(g.id))
          )
          return (
            <div key={`product-tile-${index}`} className="video-tile">
              <div className="product-tile-head">
                <Autocomplete
                  size="small"
                  fullWidth
                  options={productOptions}
                  loading={productGatesLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    Number(option?.id) === Number(value?.id)
                  }
                  value={
                    gate ||
                    (product.productGateId
                      ? { id: Number(product.productGateId), name: product.label || "Unknown product" }
                      : null)
                  }
                  onChange={(_, gate) => handleSelectProduct(index, gate)}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Program"
                      placeholder={
                        productGatesLoading ? "Loading programs…" : "Select a program"
                      }
                      helperText={
                        gate
                          ? `Members with access to "${gate.name}" will see this video.`
                          : "Pick which program this video should play for."
                      }
                    />
                  )}
                />
                {canEdit && (
                  <Tooltip title="Remove this program mapping">
                    <IconButton size="small" onClick={() => onRemoveProduct(index)}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>

              <VideoTile
                title={gate?.name || product.label || "Untitled program"}
                pillLabel="Program"
                pillKind="tier-pill tier-product"
                rawValue={product.vimeoId || ""}
                onChange={(value) => onChangeProduct(index, "vimeoId", value)}
                canEdit={canEdit}
                onPreview={onPreview}
                helperEmpty="Paste the Vimeo link for this program's welcome video."
              />
            </div>
          )
        })}
        {tieredProducts.length === 0 && (
          <div className="welcome-empty-products" style={{ gridColumn: "1 / -1" }}>
            <h4>No program-specific videos yet</h4>
            <p>
              Add a program below to play a different welcome video for members who joined through
              a specific course or product (for example, Winning the Game of Business). If a member
              doesn't have a program-specific video, they'll see the fallback above or their
              membership-tier video.
            </p>
          </div>
        )}
      </div>

      {canEdit && (
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Tooltip
            title={
              productGates.length === 0
                ? "No programs are available yet. Add programs in Subscriptions → Product Gates first."
                : "Add a program-specific welcome video"
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={onAddProduct}
                disabled={productGatesLoading || productGates.length === 0}
              >
                Add program video
              </Button>
            </span>
          </Tooltip>
        </div>
      )}

      {canEdit && (
        <div className="welcome-save-bar">
          <div className={`save-status ${dirty ? "dirty" : "clean"}`}>
            {dirty ? (
              <>
                <ErrorOutlineRoundedIcon fontSize="small" />
                <span>You have unsaved changes</span>
              </>
            ) : (
              <>
                <CheckCircleRoundedIcon fontSize="small" />
                <span>All changes saved</span>
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              variant="text"
              onClick={onReset}
              disabled={!dirty || saving || loading}
            >
              Discard changes
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={onSave}
              disabled={!dirty || saving || loading}
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Onboarding() {
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()

  const [welcomeVideoConfig, setWelcomeVideoConfig] = useState(
    getDefaultWelcomeVideoConfig()
  )
  const [welcomeVideoSavedConfig, setWelcomeVideoSavedConfig] = useState(
    getDefaultWelcomeVideoConfig()
  )
  const [welcomeVideoUpdatedAt, setWelcomeVideoUpdatedAt] = useState("")
  const [welcomeVideoLoading, setWelcomeVideoLoading] = useState(true)
  const [welcomeVideoSaving, setWelcomeVideoSaving] = useState(false)
  const [productGates, setProductGates] = useState([])
  const [productGatesLoading, setProductGatesLoading] = useState(false)
  const [previewVideo, setPreviewVideo] = useState(null) // { title, vimeoId }

  const canView =
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_LIST]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_EDIT])
  const canEdit =
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_EDIT]) ||
    AccessControl.VerifyAccess([PERMISSIONS.MODAL.MODAL_ADD])

  const loadWelcomeVideoConfig = useCallback(async () => {
    if (!canView) {
      setWelcomeVideoLoading(false)
      return
    }
    setWelcomeVideoLoading(true)
    try {
      const response = await getOnboardingWelcomeVideoConfig()
      verifyResponse(response)
      const next = resolveWelcomeVideoResponseConfig(
        response,
        getDefaultWelcomeVideoConfig()
      )
      setWelcomeVideoConfig(next)
      setWelcomeVideoSavedConfig(next)
      setWelcomeVideoUpdatedAt(resolveResponseUpdatedAt(response))
    } catch {
      enqueueSnackbar("Failed to load onboarding welcome video config.", {
        variant: "warning",
      })
    } finally {
      setWelcomeVideoLoading(false)
    }
  }, [canView, enqueueSnackbar])

  const loadProductGates = useCallback(async () => {
    if (!canView) return
    setProductGatesLoading(true)
    try {
      const response = await listProductGates()
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : []
      setProductGates(
        list
          .map((item) => ({
            id: Number(item?.id || 0),
            name: String(item?.name || "").trim(),
            code: String(item?.code || "").trim(),
            active: item?.active !== false,
          }))
          .filter((item) => item.id > 0 && item.name && item.active)
      )
    } catch {
      // Non-blocking: products list is just a convenience picker.
      setProductGates([])
    } finally {
      setProductGatesLoading(false)
    }
  }, [canView])

  useEffect(() => {
    loadWelcomeVideoConfig()
    loadProductGates()
  }, [loadWelcomeVideoConfig, loadProductGates])

  const updateWelcomeTier = (tierKey, value) => {
    setWelcomeVideoConfig((previous) => ({
      ...previous,
      tiers: {
        ...previous.tiers,
        [tierKey]: value,
      },
    }))
  }

  const updateWelcomeProductDefault = (value) => {
    setWelcomeVideoConfig((previous) => ({
      ...previous,
      productDefault: value,
    }))
  }

  const updateWelcomeProduct = (index, key, value) => {
    setWelcomeVideoConfig((previous) => ({
      ...previous,
      products: (previous.products || []).map((item, productIndex) =>
        productIndex === index ? { ...item, [key]: value } : item
      ),
    }))
  }

  const addWelcomeProduct = () => {
    setWelcomeVideoConfig((previous) => {
      const usedIds = new Set(
        (previous.products || []).map((p) => Number(p.productGateId)).filter((id) => id > 0)
      )
      const firstAvailable = (productGates || []).find((g) => !usedIds.has(Number(g.id)))
      const next = firstAvailable
        ? { productGateId: Number(firstAvailable.id), label: firstAvailable.name, vimeoId: "" }
        : { productGateId: 0, label: "", vimeoId: "" }
      return {
        ...previous,
        products: [...(previous.products || []), next],
      }
    })
  }

  const removeWelcomeProduct = (index) => {
    setWelcomeVideoConfig((previous) => ({
      ...previous,
      products: (previous.products || []).filter(
        (_, productIndex) => productIndex !== index
      ),
    }))
  }

  const handleSaveWelcomeVideos = async () => {
    if (!canEdit) return
    setWelcomeVideoSaving(true)
    try {
      const tiers = {}
      for (const key of TIER_KEYS) {
        const id = parseVimeoIdValue(welcomeVideoConfig?.tiers?.[key])
        if (id) tiers[key] = id
      }
      const productDefault = parseVimeoIdValue(welcomeVideoConfig?.productDefault)
      const products = (welcomeVideoConfig?.products || [])
        .map((item) => ({
          productGateId: Math.max(0, Math.floor(Number(item?.productGateId || 0))),
          label: String(item?.label || "").trim(),
          vimeoId: parseVimeoIdValue(item?.vimeoId),
        }))
        .filter((item) => item.productGateId > 0 && item.vimeoId)
      const payload = { tiers, productDefault, products }
      const response = await saveOnboardingWelcomeVideoConfig(payload)
      verifyResponse(response)
      const next = resolveWelcomeVideoResponseConfig(response, payload)
      setWelcomeVideoConfig(next)
      setWelcomeVideoSavedConfig(next)
      setWelcomeVideoUpdatedAt(resolveResponseUpdatedAt(response))
      enqueueSnackbar("Onboarding welcome videos saved.", { variant: "success" })
    } catch {
      enqueueSnackbar("Failed to save onboarding welcome videos.", {
        variant: "warning",
      })
    } finally {
      setWelcomeVideoSaving(false)
    }
  }

  const handleResetWelcomeVideos = () => {
    setWelcomeVideoConfig(welcomeVideoSavedConfig)
  }

  const productGatesById = useMemo(() => {
    const map = new Map()
    for (const gate of productGates) {
      map.set(Number(gate.id), gate)
    }
    return map
  }, [productGates])

  const usedProductGateIds = useMemo(
    () =>
      new Set(
        (welcomeVideoConfig?.products || [])
          .map((p) => Number(p.productGateId))
          .filter((id) => id > 0)
      ),
    [welcomeVideoConfig?.products]
  )

  const isWelcomeVideoDirty = useMemo(
    () =>
      JSON.stringify(welcomeVideoConfig) !==
      JSON.stringify(welcomeVideoSavedConfig),
    [welcomeVideoConfig, welcomeVideoSavedConfig]
  )

  return (
    <StyledModal>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">Onboarding</h1>
          <p className="page-subtitle">
            Choose the welcome video new members see on the first step of <code>/onboarding</code>.
            Edit the day-by-day messages in <strong>Welcome Series</strong> below.
          </p>
        </div>
      </PageHeading>

      <div className="page-surface">
        {!canView ? (
          <div className="empty-row">
            You do not have permission to view onboarding configuration.
          </div>
        ) : (
          <div className="onboarding-panel">
            <Alert severity="info">
              Looking for the day-by-day welcome popups? They moved to{" "}
              <strong>User Management → Welcome Series</strong>.
            </Alert>

            <WelcomeVideoSection
              config={welcomeVideoConfig}
              productGates={productGates}
              productGatesById={productGatesById}
              productGatesLoading={productGatesLoading}
              usedProductGateIds={usedProductGateIds}
              updatedAt={welcomeVideoUpdatedAt}
              loading={welcomeVideoLoading}
              saving={welcomeVideoSaving}
              dirty={isWelcomeVideoDirty}
              canEdit={canEdit}
              onChangeTier={updateWelcomeTier}
              onChangeProductDefault={updateWelcomeProductDefault}
              onChangeProduct={updateWelcomeProduct}
              onRemoveProduct={removeWelcomeProduct}
              onAddProduct={addWelcomeProduct}
              onSave={handleSaveWelcomeVideos}
              onReset={handleResetWelcomeVideos}
              onPreview={(payload) => setPreviewVideo(payload)}
            />
          </div>
        )}
      </div>

      <Dialog
        open={!!previewVideo}
        onClose={() => setPreviewVideo(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ paddingBottom: 6 }}>
          {previewVideo?.title || "Welcome video preview"}
        </DialogTitle>
        <DialogContent style={{ padding: "0 16px 16px" }}>
          {previewVideo?.vimeoId ? (
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
                borderRadius: 12,
                overflow: "hidden",
                background: "#0f172a",
              }}
            >
              <iframe
                key={previewVideo.vimeoId}
                src={`https://player.vimeo.com/video/${previewVideo.vimeoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`}
                title={previewVideo.title || "Welcome video preview"}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
              />
            </div>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewVideo(null)}>Close</Button>
          {previewVideo?.vimeoId ? (
            <Button
              component="a"
              href={`https://vimeo.com/${previewVideo.vimeoId}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewRoundedIcon />}
            >
              Open on Vimeo
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <LoadingScreen
        loading={welcomeVideoLoading || welcomeVideoSaving}
      />
    </StyledModal>
  )
}
